import { BluetoothLowEnergyAdapterProvider } from './adapter/BluetoothLowEnergyAdapterProvider';
import { BluetoothLowEnergyNamespaces } from './adapter/AbstractBluetoothLowEnergyAdapter';
import { BluetoothLowEnergyEventChannel, IBluetoothLowEnergyEvent, IBluetoothLowEnergyEventSubscription } from './BluetoothLowEnergyEventChannel';

const NAMESPACE = BluetoothLowEnergyNamespaces.CENTRAL;

/**
 * The central role: finding a peripheral, connecting to it, and reading, writing and subscribing to
 * the characteristics it hosts. This is the role a tool wants when talking to a device — a scale, a
 * sensor, a pair of glasses.
 *
 * The sequence is fixed. Open the event channel, scan until the target appears, connect, negotiate a
 * packet size, discover services, then operate on characteristics. Discovery is not optional: the
 * handles every characteristic operation uses come from it.
 *
 * Three behaviours are worth knowing before relying on them, because none is visible in a method
 * signature:
 *
 * - **Discovery expands short identifiers.** A peripheral advertising `180F` is discovered as the full
 *   128-bit form, because discovery reports what the platform found rather than what the peer named.
 *   Comparing a discovered identifier against a short constant needs the constant expanded first.
 * - **Values are Base64 in both directions.** Nothing on the air is Base64; it exists because these
 *   calls cross a JavaScript bridge and JSON has no binary type. Passing plain text where Base64 is
 *   expected only throws for about one string length in four — the rest decode to the wrong bytes and
 *   are written as-is. Encode explicitly.
 * - **Nothing fragments.** A value larger than the negotiated packet size is truncated rather than
 *   split, and the two write types truncate differently: with a response, the peripheral keeps the
 *   first 512 bytes and answers success while this side is told the write failed, so retrying writes
 *   those bytes twice. Splitting a long value is this tool's job, and the split has to be measured in
 *   decoded bytes rather than Base64 characters — 20 bytes encode to 28 characters.
 *
 * Only available inside the CandyCane application. On the Velocity browser every method rejects.
 *
 * ## Errors
 *
 * A rejection carries `{ code, message }`. Shared codes: `INVALID_OPTIONS`, `UNKNOWN_METHOD`,
 * `INITIALIZATION_ERROR`, `PLUGIN_NOT_READY`.
 *
 * Codes specific to this role: `SCAN_START_FAILED`, `SCAN_STOP_FAILED`, `CONNECT_FAILED`,
 * `DISCONNECT_FAILED`, `DISCOVER_SERVICES_FAILED`, `READ_FAILED`, `WRITE_FAILED`,
 * `READ_DESCRIPTOR_FAILED`, `WRITE_DESCRIPTOR_FAILED`, `SUBSCRIBE_FAILED`, `UNSUBSCRIBE_FAILED`,
 * `REQUEST_MTU_FAILED`, `CONNECTION_PRIORITY_FAILED`.
 *
 * Note that option validation happens natively, not here: this library forwards `config` verbatim, so
 * a missing required field surfaces as `INVALID_OPTIONS` from the bridge rather than as a local throw.
 */
export class BluetoothLowEnergyCentralService {
    /**
     * Starts scanning, reporting each advertiser found as a `scanResult` event.
     *
     * Stop as soon as the target is found: scanning is expensive on the radio and the platform
     * rate-limits repeated starts. Below Android 12, check
     * `BluetoothLowEnergyAdapterService.isLocationServicesEnabled` first — with location services off a
     * scan finds nothing and reports no error.
     *
     * @param config optional `{ filter?: { serviceUUIDs?: string[], deviceName?: string, deviceAddress?: string } }`.
     *   With no filter, every advertiser in range is reported.
     * @return {Promise} resolves `{ scanning: true }`.
     */
    public static startScan(config?: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'startScan', config);
    }

    /**
     * Stops scanning.
     *
     * @return {Promise} resolves `{ scanning: false }`.
     */
    public static stopScan(): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'stopScan');
    }

    /**
     * Connects to a peripheral, reporting later transitions as `connectionState` events.
     *
     * @param config `{ deviceAddress: string, autoConnect?: boolean }`. `autoConnect` keeps the platform
     *   retrying in the background: slower to connect, but survives the peer going out of range.
     * @return {Promise} resolves `{ deviceAddress: string }` once connected.
     */
    public static connect(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'connect', config);
    }

    /**
     * Disconnects one peripheral and releases its platform client.
     *
     * Disconnect everything the session connected. The platform pools these clients, and one left open
     * holds a slot until the application is killed.
     *
     * @param config `{ deviceAddress: string }`.
     * @return {Promise} resolves `{ deviceAddress: string }`.
     */
    public static disconnect(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'disconnect', config);
    }

    /**
     * Discovers what the connected peripheral hosts. Required before any characteristic operation.
     *
     * @param config `{ deviceAddress: string }`.
     * @return {Promise} resolves `{ services: [{ uuid, characteristics: [{ uuid, properties, descriptorUUIDs }] }] }`,
     *   with identifiers in expanded 128-bit form.
     */
    public static discoverServices(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'discoverServices', config);
    }

    /**
     * Reads a characteristic.
     *
     * @param config `{ deviceAddress: string, serviceUUID: string, characteristicUUID: string }`.
     * @return {Promise} resolves `{ value: string }`, Base64.
     */
    public static readCharacteristic(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'readCharacteristic', config);
    }

    /**
     * Writes a characteristic.
     *
     * @param config `{ deviceAddress, serviceUUID, characteristicUUID, value, writeType? }`, where
     *   `value` is Base64 and `writeType` is `'WITH_RESPONSE'` (the default) or `'WITHOUT_RESPONSE'`.
     *   An oversized value is truncated rather than rejected — see the class documentation, because the
     *   acknowledged form reports failure after the peer has already kept the bytes.
     * @return {Promise} resolves `{ written: true }`.
     */
    public static writeCharacteristic(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'writeCharacteristic', config);
    }

    /**
     * Reads a descriptor.
     *
     * @param config `{ deviceAddress, serviceUUID, characteristicUUID, descriptorUUID }`.
     * @return {Promise} resolves `{ value: string }`, Base64.
     */
    public static readDescriptor(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'readDescriptor', config);
    }

    /**
     * Writes a descriptor. For descriptors a peer defines itself — subscribing goes through
     * {@link subscribeToCharacteristic} rather than by writing the subscription descriptor directly.
     *
     * @param config `{ deviceAddress, serviceUUID, characteristicUUID, descriptorUUID, value }`, `value` Base64.
     * @return {Promise} resolves `{ written: true }`.
     */
    public static writeDescriptor(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'writeDescriptor', config);
    }

    /**
     * Subscribes to a characteristic, reporting each value the peripheral pushes as a
     * `characteristicValue` event.
     *
     * @param config `{ deviceAddress, serviceUUID, characteristicUUID }`.
     * @return {Promise} resolves `{ subscribed: true }`.
     */
    public static subscribeToCharacteristic(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'subscribeToCharacteristic', config);
    }

    /**
     * Unsubscribes from a characteristic.
     *
     * @param config `{ deviceAddress, serviceUUID, characteristicUUID }`.
     * @return {Promise} resolves `{ subscribed: false }`.
     */
    public static unsubscribeFromCharacteristic(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'unsubscribeFromCharacteristic', config);
    }

    /**
     * Asks the peripheral for a larger packet size and reports what was granted.
     *
     * The peer decides, so the granted figure is usually not the requested one, and the usable payload
     * is a few bytes smaller again because of protocol overhead. Negotiate before sending anything of
     * size: until this succeeds the packet size is the Bluetooth default of 23 bytes.
     *
     * @param config `{ deviceAddress: string, mtu: number }`.
     * @return {Promise} resolves `{ mtu: number }`, the granted size.
     */
    public static requestMTU(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'requestMTU', config);
    }

    /**
     * Asks for a different balance between latency and battery on a connection.
     *
     * @param config `{ deviceAddress: string, priority: 'BALANCED' | 'HIGH' | 'LOW_POWER' }`.
     * @return {Promise} resolves `{ priority: string }`.
     */
    public static requestConnectionPriority(config: any): Promise<any> {
        return BluetoothLowEnergyAdapterProvider.get().call(NAMESPACE, 'requestConnectionPriority', config);
    }

    /**
     * Starts receiving this role's events and delivers each to `handler`.
     *
     * Open this before scanning or connecting. Events produced with no channel open are dropped.
     *
     * | `event.type` | `event.payload` |
     * |---|---|
     * | `scanResult` | `{ device: { address, name }, rssi, serviceUUIDs, connectable }` |
     * | `connectionState` | `{ deviceAddress, state }` |
     * | `characteristicValue` | `{ deviceAddress, serviceUUID, characteristicUUID, value }` |
     *
     * Note the asymmetry: a scan result carries the peer as `payload.device.address`, while the other
     * two use a flat `payload.deviceAddress`. Reaching for `payload.deviceAddress` on a `scanResult`
     * yields `undefined`, not an error.
     *
     * @param handler invoked once per event.
     * @return {Promise} resolves a subscription whose `off()` detaches the handler. Prefer `off()` over
     *   {@link closeEventChannel} for per-component cleanup — closing is tool-level and stops the
     *   stream for every subscriber in this frame.
     */
    public static openEventChannel(handler: (event: IBluetoothLowEnergyEvent) => void): Promise<IBluetoothLowEnergyEventSubscription> {
        return BluetoothLowEnergyEventChannel.open(NAMESPACE, handler);
    }

    /**
     * Stops receiving this role's events and detaches every handler.
     *
     * @return {Promise} resolves `{ closed: boolean }`, false when no channel was open.
     */
    public static closeEventChannel(): Promise<any> {
        return BluetoothLowEnergyEventChannel.close(NAMESPACE);
    }
}
